import { useState, useEffect } from 'react';
import { organizationsService, Organization, OrganizationMember, OrganizationInvitation } from '@/lib/api';

export const useOrganizations = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await organizationsService.getUserOrganizations();
      
      if (response.success && response.data) {
        setOrganizations(response.data.organizations);
      } else {
        setError(response.error || 'Failed to fetch organizations');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const createOrganization = async (name: string) => {
    try {
      const response = await organizationsService.createOrganization({ name });
      
      if (response.success && response.data) {
        setOrganizations(prev => [response.data!, ...prev]);
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to create organization');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  };

  const updateOrganization = async (organizationId: string, updates: { name?: string; support_email?: string }) => {
    try {
      const response = await organizationsService.updateOrganization(organizationId, updates);
      
      if (response.success && response.data) {
        setOrganizations(prev => 
          prev.map(org => org.id === organizationId ? response.data! : org)
        );
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to update organization');
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  };

  const deleteOrganization = async (organizationId: string) => {
    try {
      const response = await organizationsService.deleteOrganization(organizationId);
      
      if (response.success) {
        setOrganizations(prev => prev.filter(org => org.id !== organizationId));
      } else {
        throw new Error(response.error || 'Failed to delete organization');
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  };

  const uploadLogo = async (organizationId: string, file: File) => {
    try {
      const response = await organizationsService.uploadLogo(organizationId, file);
      
      if (response.success && response.data) {
        // Update the organization with the new logo URL
        setOrganizations(prev => 
          prev.map(org => 
            org.id === organizationId 
              ? { ...org, logo_url: response.data!.logo_url }
              : org
          )
        );
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  return {
    organizations,
    loading,
    error,
    refetch: fetchOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    uploadLogo,
  };
};

export const useOrganizationMembers = (organizationId: string | null) => {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await organizationsService.getOrganizationMembers(organizationId);
      
      if (response.success && response.data) {
        setMembers(response.data.members);
      } else {
        setError(response.error || 'Failed to fetch members');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [organizationId]);

  const addMember = async (email: string, role: 'admin' | 'member') => {
    if (!organizationId) throw new Error('Organization ID is required');

    try {
      const response = await organizationsService.addMember(organizationId, { email, role });
      
      if (response.success && response.data) {
        setMembers(prev => [...prev, response.data!]);
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to add member');
      }
    } catch (error) {
      console.error('Error adding member:', error);
      throw error;
    }
  };

  const updateMemberRole = async (memberUserId: string, role: 'admin' | 'member') => {
    if (!organizationId) throw new Error('Organization ID is required');

    try {
      const response = await organizationsService.updateMemberRole(organizationId, memberUserId, { role });
      
      if (response.success && response.data) {
        setMembers(prev => 
          prev.map(member => 
            member.user_id === memberUserId ? response.data! : member
          )
        );
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to update member role');
      }
    } catch (error) {
      console.error('Error updating member role:', error);
      throw error;
    }
  };

  const removeMember = async (memberUserId: string) => {
    if (!organizationId) throw new Error('Organization ID is required');

    try {
      const response = await organizationsService.removeMember(organizationId, memberUserId);
      
      if (response.success) {
        setMembers(prev => prev.filter(member => member.user_id !== memberUserId));
      } else {
        throw new Error(response.error || 'Failed to remove member');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      throw error;
    }
  };

  return {
    members,
    loading,
    error,
    refetch: fetchMembers,
    addMember,
    updateMemberRole,
    removeMember,
  };
};

export const useOrganizationInvitations = (organizationId: string | null) => {
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvitations = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await organizationsService.getInvitations(organizationId);
      
      if (response.success && response.data) {
        setInvitations(response.data.invitations);
      } else {
        setError(response.error || 'Failed to fetch invitations');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [organizationId]);

  const createInvitation = async (email: string, role: 'admin' | 'member') => {
    if (!organizationId) throw new Error('Organization ID is required');

    try {
      const response = await organizationsService.createInvitation(organizationId, { email, role });
      
      if (response.success && response.data) {
        setInvitations(prev => [...prev, response.data!]);
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to create invitation');
      }
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw error;
    }
  };

  const revokeInvitation = async (email: string) => {
    if (!organizationId) throw new Error('Organization ID is required');

    try {
      const response = await organizationsService.revokeInvitation(organizationId, email);
      
      if (response.success) {
        setInvitations(prev => prev.filter(invitation => invitation.email !== email));
      } else {
        throw new Error(response.error || 'Failed to revoke invitation');
      }
    } catch (error) {
      console.error('Error revoking invitation:', error);
      throw error;
    }
  };

  const acceptInvitation = async (token: string) => {
    if (!organizationId) throw new Error('Organization ID is required');

    try {
      const response = await organizationsService.acceptInvitation(organizationId, { token });
      
      if (response.success && response.data) {
        // Remove the invitation from the list since it's been accepted
        setInvitations(prev => prev.filter(invitation => invitation.token !== token));
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  };

  return {
    invitations,
    loading,
    error,
    refetch: fetchInvitations,
    createInvitation,
    revokeInvitation,
    acceptInvitation,
  };
};